/*
 * What the inbox says while a campaign's history is still importing. The API reports one
 * `{ done, total }` per campaign; this turns that into one row of text, one bar and the few
 * moments worth announcing to a screen reader. Pure: the clock and the page stay in the component.
 */
export interface BackfillCampaign {
  readonly campaignId: string;
  readonly campaignName: string;
  readonly total: number;
  readonly done: number;
  readonly draftsPerHour: number | null;
  readonly maxDraftAgeDays: number;
}

export interface BackfillSummary {
  /** The campaign's name when exactly one is importing, else null (the row counts campaigns). */
  readonly name: string | null;
  readonly campaigns: number;
  readonly done: number;
  readonly total: number;
  readonly waiting: number;
  readonly draftsPerHour: number | null;
  /** Whole percent, 0 to 99 while importing: a full bar means finished, never "almost". */
  readonly percent: number;
}

const importing = (campaign: BackfillCampaign): boolean => campaign.done < campaign.total;

export function importingCampaigns(campaigns: readonly BackfillCampaign[]): BackfillCampaign[] {
  return campaigns.filter(importing);
}

/** One summary over every campaign still importing, or null when none is. */
export function summarizeBackfill(campaigns: readonly BackfillCampaign[]): BackfillSummary | null {
  const active = importingCampaigns(campaigns);
  const [first] = active;
  if (!first) return null;
  const done = active.reduce((sum, campaign) => sum + campaign.done, 0);
  const total = active.reduce((sum, campaign) => sum + campaign.total, 0);
  return {
    name: active.length === 1 ? first.campaignName : null,
    campaigns: active.length,
    done,
    total,
    waiting: total - done,
    draftsPerHour: first.draftsPerHour,
    percent: Math.min(99, Math.floor((done / total) * 100)),
  };
}

export function backfillCountText(
  summary: Pick<BackfillSummary, "waiting" | "draftsPerHour">,
): string {
  return summary.draftsPerHour === null
    ? `${summary.waiting} waiting`
    : `${summary.waiting} waiting, ${summary.draftsPerHour} per hour`;
}

/**
 * The quarter the import has passed. The poll runs every 15 seconds, so announcing each count
 * would talk over the rep all afternoon; announcing a change of quarter says the same thing four
 * times per import.
 */
export function backfillMilestone(percent: number): 0 | 25 | 50 | 75 {
  if (percent >= 75) return 75;
  if (percent >= 50) return 50;
  if (percent >= 25) return 25;
  return 0;
}
